/**
 * Phase 1 (custom roles) — Agency Portal Roles API client.
 *
 * Thin fetch wrapper matching the existing identity-api convention
 * (`src/lib/api/identity/identity-api.ts`). RTK Query is intentionally NOT used
 * — no slice in this repo does yet.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}` — the
 * same agency `emp_` Bearer token every other `/agency/*` call uses. Per
 * CLAUDE.md this token lives in Redux ONLY and is never read from localStorage.
 * The httpOnly agency-session cookie is also sent (`credentials: 'include'`) so
 * these calls authenticate on a cold reload exactly like the identity calls.
 *
 * Base URL: `CENTRAL_API_BASE_URL` from `src/lib/constants/central-api.ts`,
 * which already resolves to `${env-base}/api/v1/` across DEV/STAGING/LIVE.
 *
 * Backend contract:
 *   GET    /api/v1/agency/roles        → { data: AgencyRole[], all_capabilities: CapabilityDef[], all_platforms: PlatformDef[] }
 *                                        (the 5 system roles auto-provision on first GET)
 *   PATCH  /api/v1/agency/roles/{id}   → { data: AgencyRole }
 *      body { name?, description?, capabilities?: string[], brand_access?, mcp_access? }
 *      - editing the OWNER role's `capabilities` → 422 { error: 'owner_role_locked' }
 *      - the OWNER role's `mcp_access` is forced to `{ mode: 'all' }` server-side
 *      - invalid capability key → 422
 *      - cross-org id → 403
 *   POST   /api/v1/agency/roles        → 201 { data: AgencyRole }
 *      body { name, description?, capabilities[], brand_access, mcp_access? }
 *      (`key` is auto-slugged from the name server-side)
 *   DELETE /api/v1/agency/roles/{id}   → 204/200 (success)
 *      - 403 for system roles (cannot be deleted)
 *      - 409 { assigned_employee_count: N } when employees still hold the role
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- wire types ----------

export type BrandAccess = 'all' | 'assigned';

/** MCP connector access mode for a role. */
export type McpAccessMode = 'all' | 'allowlist';

/**
 * Which MCP connectors (platforms) members of a role may use.
 *
 *  - `{ mode: 'all' }`        → every connected platform (no list).
 *  - `{ mode: 'allowlist', platforms: [...] }` → only the listed platform keys.
 *
 * The owner role is always forced to `{ mode: 'all' }` server-side.
 */
export interface McpAccess {
  mode: McpAccessMode;
  /** Allowed platform keys — only meaningful when `mode === 'allowlist'`. */
  platforms?: string[];
}

export interface AgencyRole {
  id: string;
  /** Stable machine key (e.g. `owner`, `team_lead`). System roles only. */
  key: string;
  name: string;
  description: string;
  /** Effective capability keys granted to this role. */
  capabilities: string[];
  /** Whether members of this role can act on all brands or only assigned ones. */
  brand_access: BrandAccess;
  /** True for the 5 auto-provisioned system roles (owner/team_lead/…). */
  is_system: boolean;
  /** Whether the role may be assigned to employees. */
  is_assignable: boolean;
  /**
   * Which MCP connectors this role can use. Optional on the wire for
   * backwards-compatibility with pre-Phase-2 backends; the UI defaults a
   * missing value to `{ mode: 'all' }`.
   */
  mcp_access?: McpAccess;
}

/** Platform dictionary entry from `all_platforms` (the MCP connector picker). */
export interface PlatformDef {
  key: string;
  /** Human label, e.g. "Meta Ads", "Google Ads". */
  label: string;
}

/** Capability dictionary entry from `all_capabilities`. */
export interface CapabilityDef {
  key: string;
  /** Human action label, e.g. "View clients". */
  label: string;
  /** Grouping bucket for the toggle matrix UI. */
  group: CapabilityGroup;
}

export type CapabilityGroup =
  | 'Clients'
  | 'Proposals'
  | 'Team'
  | 'Skills'
  | 'Billing'
  | 'Notifications'
  | 'Stats'
  | 'Settings'
  // Tolerate any future group the backend adds without breaking the grouping UI.
  | (string & {});

export interface ListRolesResponse {
  data: AgencyRole[];
  all_capabilities: CapabilityDef[];
  /** Connector dictionary backing the per-role MCP-access allowlist picker. */
  all_platforms: PlatformDef[];
}

/** Partial update — only the changed fields should be sent. */
export interface UpdateRolePatch {
  name?: string;
  description?: string;
  capabilities?: string[];
  brand_access?: BrandAccess;
  /** Owner is forced to `{ mode: 'all' }` server-side regardless of what we send. */
  mcp_access?: McpAccess;
}

/** Body for creating a custom role. `key` is auto-slugged from `name` server-side. */
export interface CreateRoleBody {
  name: string;
  description?: string;
  capabilities: string[];
  brand_access: BrandAccess;
  mcp_access?: McpAccess;
}

/** Error shape the backend uses for the locked-owner / invalid-key 422s. */
export interface RolesApiError extends Error {
  status?: number;
  /** e.g. `owner_role_locked` — present on the structured 422 bodies. */
  code?: string;
  /** The sentence the API gives for a refusal (403 `insufficient_rank`). */
  reason?: string;
  /**
   * Populated on the `409` returned by DELETE when employees still hold the
   * role — the number of employees that must be reassigned first.
   */
  assigned_employee_count?: number;
}

// ---------- internal helpers (mirrors identity-api) ----------

function getAuthHeader(): Record<string, string> {
  const token = store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[roles-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
    // Send the httpOnly agency-session cookie so these calls authenticate on a
    // cold reload the same way the identity (`/me`) calls do.
    credentials: 'include',
  });

  if (!response.ok) {
    let detail = '';
    let code: string | undefined;
    let reason: string | undefined;
    let assignedEmployeeCount: number | undefined;
    try {
      const body = await parseJson<{
        message?: string;
        error?: string;
        reason?: string;
        assigned_employee_count?: number;
      }>(response);
      // The backend returns the machine code under `error` (e.g.
      // `owner_role_locked`); `message` is the human string when present.
      code = body?.error;
      detail = body?.message || body?.error || '';
      if (typeof body?.reason === 'string' && body.reason.trim()) {
        reason = body.reason;
      }
      // DELETE conflict (409): how many employees still hold the role.
      if (typeof body?.assigned_employee_count === 'number') {
        assignedEmployeeCount = body.assigned_employee_count;
      }
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as RolesApiError;
    err.status = response.status;
    err.code = code;
    if (reason) err.reason = reason;
    err.assigned_employee_count = assignedEmployeeCount;
    throw err;
  }

  return parseJson<T>(response);
}

// ---------- endpoints ----------

/**
 * GET /api/v1/agency/roles — list the org's roles + the capability dictionary
 * + the platform (MCP connector) dictionary. The 5 system roles auto-provision
 * server-side on the first call.
 */
export async function listRoles(): Promise<ListRolesResponse> {
  const res = await request<Partial<ListRolesResponse>>('agency/roles', {
    method: 'GET',
  });
  // Defensive defaults so the page renders a clean empty state rather than
  // throwing if the envelope is missing a field on a 200.
  return {
    data: res.data ?? [],
    all_capabilities: res.all_capabilities ?? [],
    all_platforms: res.all_platforms ?? [],
  };
}

/**
 * PATCH /api/v1/agency/roles/{id} — update only the changed fields.
 *
 * Throws a `RolesApiError` on failure; callers should special-case
 * `err.code === 'owner_role_locked'` (422) for a friendly message. Never send a
 * `capabilities` patch for the owner role — it is always full and will 422.
 */
export async function updateRole(
  id: string,
  patch: UpdateRolePatch
): Promise<AgencyRole> {
  const res = await request<{ data: AgencyRole }>(
    `agency/roles/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }
  );
  return res.data;
}

/**
 * POST /api/v1/agency/roles — create a custom role. The backend auto-slugs the
 * `key` from `name` and returns `201 { data: AgencyRole }`. New roles are
 * always custom (`is_system === false`).
 *
 * Throws a `RolesApiError` on failure (e.g. 422 for an invalid capability key
 * or a name collision).
 */
export async function createRole(body: CreateRoleBody): Promise<AgencyRole> {
  const res = await request<{ data: AgencyRole }>('agency/roles', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.data;
}

/**
 * DELETE /api/v1/agency/roles/{id} — delete a custom role.
 *
 * Throws a `RolesApiError` on failure; callers should special-case:
 *   - `err.status === 403` → system role, cannot be deleted.
 *   - `err.status === 409` → employees still hold the role; the count to
 *     reassign is on `err.assigned_employee_count`.
 */
export async function deleteRole(id: string): Promise<void> {
  await request<unknown>(`agency/roles/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}
